import { inject, Injectable } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { RouterStateSnapshot, TitleStrategy } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'

export const ATLAS_NAME = 'Atlas'

/**
 * Names the page in the browser tab.
 *
 * Atlas is a set of screens a support person keeps open beside each other — the inbox in one tab, a
 * conversation in another, the environment ledger in a third — so a row of tabs that all read
 * "Atlas" is precisely what the title must not be.
 *
 * The routes carry translation keys rather than words, as the main app's AppTitleStrategy does. The
 * support screens Atlas hosts are already translated, so a tab that could only ever say the English
 * name would be the odd one out.
 */
@Injectable({ providedIn: 'root' })
export class AtlasTitleStrategy extends TitleStrategy {
  private readonly titleService = inject(Title)
  private readonly translateService = inject(TranslateService)

  /** The key the tab should be showing, so a slow lookup cannot land on top of a newer page. */
  private wanted: string | null = null

  override updateTitle(snapshot: RouterStateSnapshot) {
    const key = this.buildTitle(snapshot) ?? null
    this.wanted = key
    if (key === null) {
      this.titleService.setTitle(ATLAS_NAME)
      return
    }
    /*
     * `get` rather than `instant`: routing starts without waiting for the translation bundle, which
     * Atlas fetches over HTTP, so the first page asked for would otherwise be titled with the key
     * itself and stay that way until something navigated. `get` waits for the load already in flight.
     */
    this.translateService.get(key).subscribe((name: string) => {
      if (this.wanted === key) {
        this.titleService.setTitle(`${name} - ${ATLAS_NAME}`)
      }
    })
  }
}
